import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import ConfigScreen from './configScreen';
import { getNextUrl } from './utils';
import { selectConfigFlowPages } from '../../../selectors';
import { productNeedsBackgroundSelection } from '../products/productConfigPredicates';

// ConfigScreen's props require `items`, which every other step passes. This
// screen drives it by `categories` and has never passed `items` — it is only
// read on the pose path — so the boundary is typed to what it actually accepts
// rather than loosening the signature every other step shares.
const CategoryConfigScreen = ConfigScreen as unknown as FC<{
  instruction: string;
  itemsDesktop: number;
  itemsMobile: number;
  categories: { id: number; name: string; items: unknown[] }[];
}>;

interface BackgroundCategory {
  id: number;
  name: string;
  background_images: unknown[];
}

// The wizard's background step.
//
// It renders the SHOOT's catalogue, which is the same grid for every slot — it
// has no idea which child it is configuring. That is why the eligibility check
// has to happen here and not in the grid: routing (getNextUrl) skips this page
// for a child with no background to composite, but routing is not the only way
// to arrive. A reload of a stored URL, a bookmark, or the multi-step
// `history.go(-keys)` jump in V3_REMOVE_ITEM_FROM_CONFIG can all land here
// directly, and the shifted slot may hold a product that takes no background.
// A tap then stores a selection the product cannot use, which the completion
// gate accepts and buildCartObject ships to the lab (ENG-2300 / ENG-2552).
//
// The single-product screen already refuses to render its picker for the same
// reason (BackgroundsConfig). This is the wizard's equivalent last-line guard.
const Background: FC = () => {
  const history = useHistory();
  const { pathname } = useLocation();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shoot = useSelector((state: any) => state.shoot);
  const productConfig = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.v3Order?.productConfig ?? {},
  );
  const pages = useSelector(selectConfigFlowPages);

  const slotIndex = Number(pathname.split('/').pop());
  const product = productConfig[slotIndex]?.product;
  const needsBackground = productNeedsBackgroundSelection(product);

  // Continue through whatever the slot still owes rather than leaving the
  // customer on a blank page. `replace`, not `push`: the URL we are refusing
  // must not stay in the history stack for them to come back to.
  useEffect(() => {
    if (needsBackground) return;

    history.replace(
      getNextUrl('background', pages, history, productConfig, shoot, null),
    );
  }, [needsBackground, history, pages, productConfig, shoot]);

  if (!needsBackground) return null;

  return (
    <CategoryConfigScreen
      instruction="Select a Background"
      itemsDesktop={6}
      itemsMobile={3}
      categories={(shoot?.backgroundOptions ?? []).map(
        (b: BackgroundCategory) => ({
          id: b.id,
          name: b.name,
          items: b.background_images,
        }),
      )}
    />
  );
};

export default Background;
